<template>
  <div class="deliver-strategy-panel" v-loading="loading">
    <section class="panel-header">
      <div>
        <h3>Deliver 数据策略</h3>
        <p>MyDeliverV5075 / MyDeliverV2</p>
      </div>

      <div class="header-actions">
        <el-switch
          v-model="syncCopy"
          inline-prompt
          active-text="同步"
          inactive-text="本机"
        />
        <el-button :icon="Refresh" plain :loading="loading" @click="fetchAll">
          刷新
        </el-button>
        <el-button
          :icon="Refresh"
          plain
          :loading="rebuildingIndex"
          @click="rebuildVinDeliverIndex"
        >
          重建 VIN 索引
        </el-button>
        <el-popconfirm
          title="发布后会重写 Redis 规则，并清空 VIN Deliver 索引表。确认发布？"
          confirm-button-text="发布"
          cancel-button-text="取消"
          @confirm="handlePublish"
        >
          <template #reference>
            <el-button :icon="Promotion" type="primary" :loading="publishing">
              统一发布
            </el-button>
          </template>
        </el-popconfirm>
      </div>
    </section>

    <section class="status-strip">
      <div class="status-item">
        <span>批量规则</span>
        <strong>{{ uploadTotal }}</strong>
      </div>
      <div class="status-item">
        <span>手动规则</span>
        <strong>{{ manualTotal }}</strong>
      </div>
      <div class="status-item">
        <span>V2 手动</span>
        <strong>{{ countOf('V2', 'manual') }}</strong>
      </div>
      <div class="status-item">
        <span>VIN 索引</span>
        <el-tag :type="status.rebuildRequired ? 'warning' : 'success'" effect="plain">
          {{ status.rebuilding ? '重建中' : (status.rebuildRequired ? '待重建' : '正常') }}
        </el-tag>
      </div>
      <div class="status-item">
        <span>索引 VIN</span>
        <strong>{{ status.total || 0 }}</strong>
      </div>
      <div class="status-item">
        <span>Fallback</span>
        <el-tag :type="status.v5075FallbackV2 ? 'success' : 'info'" effect="plain">
          {{ status.v5075FallbackV2 ? 'V2 开启' : '仅 V5075' }}
        </el-tag>
      </div>
    </section>

    <div v-if="indexRebuildProgress" class="index-progress-line">
      已扫描 {{ indexRebuildProgress.scanned }} 条，
      命中 {{ indexRebuildProgress.matched }} 条，
      跳过 {{ indexRebuildProgress.skipped }} 条
    </div>

    <section class="config-section redis-actual-section">
      <div class="section-heading">
        <div>
          <h4>Redis 实际状态</h4>
          <p>当前线上生效的 MyDeliverV5075 / MyDeliverV2</p>
        </div>
        <div class="section-actions compact-actions">
          <el-button :icon="Refresh" plain :loading="loadingRedisStats" @click="fetchRedisStats">
            刷新统计
          </el-button>
          <el-button :icon="DocumentChecked" type="primary" plain :loading="verifyingRedis" @click="verifyRedis">
            校验发布
          </el-button>
        </div>
      </div>

      <el-table
        :data="redisStats"
        border
        stripe
        size="small"
        class="rule-table redis-stats-table"
        empty-text="暂无 Redis 统计"
      >
        <el-table-column prop="version" label="版本" width="86" align="center" />
        <el-table-column prop="area" label="区域" width="82" align="center" />
        <el-table-column prop="count" label="Key 数" width="96" align="center" />
        <el-table-column prop="posGroupCount" label="POS 组" width="96" align="center" />
        <el-table-column prop="keyPattern" label="扫描范围" min-width="180" show-overflow-tooltip />
        <el-table-column prop="posValue" label="POS 内容" min-width="220" show-overflow-tooltip>
          <template #default="{ row }">
            <span class="mono">{{ row.posValue || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="104" align="center" fixed="right">
          <template #default="{ row }">
            <el-button
              :icon="Search"
              link
              type="primary"
              :loading="loadingRedisKeys === `${row.version}:${row.area}`"
              @click="queryRedisRowKeys(row)"
            >
              查询
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="redisKeysResult" class="redis-keys-heading">
        <el-tag type="primary" effect="plain">
          {{ redisKeysResult.version }} / {{ redisKeysResult.area }}
        </el-tag>
        <span>{{ redisKeysResult.keyPattern }}</span>
        <strong>{{ redisKeysResult.count || 0 }} 个 Key</strong>
      </div>

      <el-table
        v-if="redisKeysResult"
        :data="redisKeysResult.keys || []"
        border
        stripe
        size="small"
        class="rule-table redis-keys-table"
        empty-text="当前行没有 Redis Key"
      >
        <el-table-column prop="pattern" label="Pattern" min-width="160">
          <template #default="{ row }">
            <span class="mono">{{ row.pattern }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="value" label="Deliver" width="110" align="center" />
        <el-table-column prop="key" label="Redis Key" min-width="260" show-overflow-tooltip>
          <template #default="{ row }">
            <span class="mono">{{ row.key }}</span>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="redisVerifyResult" class="redis-verify">
        <el-tag :type="redisVerifyResult.consistent ? 'success' : 'danger'" effect="plain">
          {{ redisVerifyResult.consistent ? '一致' : '存在差异' }}
        </el-tag>
        <span>检查 {{ redisVerifyResult.checked || 0 }} 条</span>
        <span>正常 {{ redisVerifyResult.ok || 0 }} 条</span>
        <span>缺失 {{ redisVerifyResult.missing || 0 }} 条</span>
        <span>不一致 {{ redisVerifyResult.mismatch || 0 }} 条</span>
      </div>

      <el-table
        v-if="redisVerifyResult?.issues?.length"
        :data="redisVerifyResult.issues"
        border
        size="small"
        class="rule-table"
      >
        <el-table-column prop="type" label="类型" width="90" align="center" />
        <el-table-column prop="version" label="版本" width="86" align="center" />
        <el-table-column prop="area" label="区域" width="82" align="center" />
        <el-table-column prop="pattern" label="规则" min-width="150" />
        <el-table-column prop="expected" label="MySQL" width="96" align="center" />
        <el-table-column prop="actual" label="Redis" width="96" align="center" />
        <el-table-column prop="key" label="Key" min-width="220" show-overflow-tooltip />
      </el-table>
    </section>

    <div class="work-grid">
      <section class="config-section batch-section">
        <div class="section-heading">
          <div>
            <h4>批量上传</h4>
            <p>目标版本固定为 V5075</p>
          </div>
          <div class="section-actions compact-actions batch-upload-actions">
            <el-button :icon="Upload" plain @click="chooseBatchFiles">
              选择四个文件
            </el-button>
            <el-tag effect="plain">{{ batchRuleCount }}</el-tag>
          </div>
        </div>

        <input
          ref="batchFilesInput"
          class="hidden-file-input"
          type="file"
          accept=".txt,text/plain"
          multiple
          @change="readBatchFileSet"
        />

        <div class="area-grid">
          <div v-for="area in areaOptions" :key="area.value" class="area-editor">
            <div class="area-title">
              <strong>{{ area.label }}</strong>
              <el-upload
                :auto-upload="false"
                :show-file-list="false"
                :on-change="(file) => readAreaFile(file, area.value)"
              >
                <el-button :icon="Upload" plain size="small">
                  选择文件
                </el-button>
              </el-upload>
            </div>
            <el-input
              v-model="batchAreas[area.value]"
              type="textarea"
              :rows="6"
              resize="vertical"
              placeholder="....E0A..S...    8327"
            />
          </div>
        </div>

        <el-input
          v-model="batchRemark"
          class="remark-input"
          clearable
          placeholder="批量备注"
        />

        <div class="section-actions">
          <el-button :icon="Upload" type="primary" :loading="savingBatch" @click="submitBatch">
            保存批量规则
          </el-button>
          <el-button plain @click="clearBatch">清空输入</el-button>
        </div>
      </section>

      <section class="config-section manual-section">
        <div class="section-heading">
          <div>
            <h4>手动添加</h4>
            <p>支持单条或多行粘贴</p>
          </div>
          <el-tag effect="plain">{{ manualRuleCount }}</el-tag>
        </div>

        <el-form label-position="top" class="manual-form">
          <div class="manual-grid">
            <el-form-item label="版本">
              <el-select v-model="manualForm.strategyVersion" class="full-control">
                <el-option label="V5075" value="V5075" />
                <el-option label="V2" value="V2" />
              </el-select>
            </el-form-item>
            <el-form-item label="区域">
              <el-select v-model="manualForm.area" class="full-control">
                <el-option
                  v-for="area in areaOptions"
                  :key="area.value"
                  :label="area.label"
                  :value="area.value"
                />
              </el-select>
            </el-form-item>
          </div>
          <el-form-item label="规则">
            <el-input
              v-model="manualForm.rulesText"
              type="textarea"
              :rows="9"
              resize="vertical"
              placeholder="....E0A..S...    8327"
            />
          </el-form-item>
          <el-form-item label="备注">
            <el-input v-model="manualForm.remark" clearable />
          </el-form-item>
        </el-form>

        <div class="section-actions">
          <el-button :icon="Plus" type="primary" :loading="savingManual" @click="submitManual">
            保存手动规则
          </el-button>
          <el-button plain @click="clearManual">清空输入</el-button>
        </div>
      </section>
    </div>

    <section v-if="parseErrors.length" class="config-section error-section">
      <div class="section-heading">
        <div>
          <h4>无效行</h4>
          <p>{{ parseErrors.length }} 条需要修正</p>
        </div>
        <el-button plain size="small" @click="parseErrors = []">清除</el-button>
      </div>
      <el-table :data="parseErrors" border size="small" class="rule-table">
        <el-table-column prop="area" label="区域" width="76" />
        <el-table-column prop="line" label="行号" width="76" align="center" />
        <el-table-column prop="message" label="原因" min-width="220" show-overflow-tooltip />
        <el-table-column prop="text" label="原始内容" min-width="220" show-overflow-tooltip />
      </el-table>
    </section>

    <section class="config-section rule-list-section">
      <div class="section-heading">
        <div>
          <h4>手动规则列表</h4>
          <p>手动规则：{{ manualTotal }} 条 / 批量规则：{{ uploadTotal }} 条</p>
        </div>
        <div class="list-filters">
          <el-select v-model="filters.strategyVersion" clearable placeholder="版本" @change="handleRuleFilterChange">
            <el-option label="V5075" value="V5075" />
            <el-option label="V2" value="V2" />
          </el-select>
          <el-select v-model="filters.area" clearable placeholder="区域" @change="handleRuleFilterChange">
            <el-option
              v-for="area in areaOptions"
              :key="area.value"
              :label="area.label"
              :value="area.value"
            />
          </el-select>
        </div>
      </div>

      <el-table
        :data="rules"
        border
        stripe
        size="small"
        class="rule-table"
        empty-text="暂无手动规则"
      >
        <el-table-column prop="strategyVersion" label="版本" width="86" align="center" />
        <el-table-column prop="area" label="区域" width="82" align="center" />
        <el-table-column prop="pattern" label="匹配规则" min-width="150">
          <template #default="{ row }">
            <span class="mono">{{ row.pattern }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="deliver" label="Deliver" width="96" align="center" />
        <el-table-column prop="pos" label="POS" min-width="160" show-overflow-tooltip>
          <template #default="{ row }">
            <span class="mono">{{ row.pos || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="150" show-overflow-tooltip />
        <el-table-column prop="updatedAt" label="更新时间" min-width="160" show-overflow-tooltip />
        <el-table-column label="操作" width="82" fixed="right" align="center">
          <template #default="{ row }">
            <el-button
              :icon="Delete"
              link
              type="danger"
              aria-label="删除规则"
              @click="deleteRule(row)"
            />
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        v-model:current-page="rulePage"
        v-model:page-size="rulePageSize"
        :total="manualTotal"
        :page-sizes="[50, 100, 200, 500]"
        layout="total, sizes, prev, pager, next"
        class="rule-pagination"
        @size-change="handleRulePageSizeChange"
        @current-change="fetchRules"
      />
    </section>

    <section class="config-section vin-test-section">
      <div class="section-heading">
        <div>
          <h4>VIN 测试</h4>
          <p>按当前 Redis 规则匹配</p>
        </div>
      </div>

      <div class="vin-test-row">
        <el-input
          v-model.trim="vinInput"
          class="vin-input"
          clearable
          maxlength="17"
          placeholder="输入 17 位 VIN"
          @keyup.enter="testVin"
        />
        <el-button :icon="Search" type="primary" :loading="testingVin" @click="testVin">
          测试
        </el-button>
      </div>

      <div v-if="vinResult" class="vin-result">
        <el-tag :type="vinResult.matched ? 'success' : 'info'" effect="plain">
          {{ vinResult.matched ? '命中' : '未命中' }}
        </el-tag>
        <span class="mono">{{ vinResult.vin }}</span>
        <strong>Deliver: {{ vinResult.deliver || '-' }}</strong>
        <strong>DiagMod: {{ vinResult.diagMod || '-' }}</strong>
        <span>Redis 调用 {{ vinResult.timing?.calls || 0 }} 次</span>
      </div>

      <div v-if="publishResult" class="publish-result">
        <el-tag type="success" effect="plain">已发布</el-tag>
        <span>写入 {{ publishResult.written || 0 }} 条</span>
        <span>清理 V5075 {{ publishResult.clearedV5075 || 0 }} 个 Key</span>
        <span>恢复 V2 {{ publishResult.restoredManualV2 || 0 }} 条</span>
        <span>清理 V2 {{ publishResult.clearedManualV2 || 0 }} 条</span>
        <span>{{ publishResult.vinDeliverIndex?.rebuildRequired ? 'VIN 索引待重建' : 'VIN 索引已更新' }}</span>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Delete, DocumentChecked, Plus, Promotion, Refresh, Search, Upload } from '@element-plus/icons-vue';

import {
  addFordDeliverStrategyManualRules,
  deleteFordDeliverStrategyRule,
  getFordDeliverStrategyRules,
  getFordDeliverStrategyRedisKeys,
  getFordDeliverStrategyRedisStats,
  getFordDeliverStrategyStatus,
  getFordDeliverVinIndexStatus,
  publishFordDeliverStrategy,
  rebuildFordDeliverVinIndex,
  testFordDeliverStrategyVin,
  uploadFordDeliverStrategyRules,
  verifyFordDeliverStrategyRedis,
} from '@/api/users';
import { validateDeliverBatchFiles } from '@/utils/deliverStrategyUi.mjs';

const areaOptions = [
  { label: 'US', value: 'US' },
  { label: 'CHN', value: 'CHN' },
  { label: 'XX', value: 'XX' },
  { label: 'OTHER', value: 'OTHER' },
];

const loading = ref(false);
const savingBatch = ref(false);
const savingManual = ref(false);
const publishing = ref(false);
const testingVin = ref(false);
const loadingRedisStats = ref(false);
const loadingRedisKeys = ref('');
const verifyingRedis = ref(false);
const rebuildingIndex = ref(false);
const syncCopy = ref(true);
const rules = ref([]);
const manualTotal = ref(0);
const uploadTotal = ref(0);
const rulePage = ref(1);
const rulePageSize = ref(100);
const redisStats = ref([]);
const redisKeysResult = ref(null);
const redisVerifyResult = ref(null);
const batchFilesInput = ref(null);
const status = ref({
  counts: [],
  rebuildRequired: false,
  rebuilding: false,
  total: 0,
  v5075FallbackV2: true,
});
const indexRebuildProgress = ref(null);
const batchAreas = reactive({
  US: '',
  CHN: '',
  XX: '',
  OTHER: '',
});
const batchRemark = ref('');
const manualForm = reactive({
  strategyVersion: 'V5075',
  area: 'US',
  rulesText: '',
  remark: '',
});
const filters = reactive({
  strategyVersion: '',
  area: '',
});
const parseErrors = ref([]);
const vinInput = ref('');
const vinResult = ref(null);
const publishResult = ref(null);

const unwrapData = (response) => response?.data?.data || response?.data || {};

const nonEmptyLineCount = (text) => {
  return String(text || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean).length;
};

const batchRuleCount = computed(() => {
  return Object.values(batchAreas).reduce((total, value) => total + nonEmptyLineCount(value), 0);
});

const manualRuleCount = computed(() => nonEmptyLineCount(manualForm.rulesText));

const countOf = (strategyVersion, source) => {
  const item = (status.value.counts || []).find((row) => {
    return row.strategyVersion === strategyVersion && row.source === source;
  });
  return item?.count || 0;
};

const normalizeManualRules = (items = []) => {
  return (Array.isArray(items) ? items : []).filter((rule) => {
    return String(rule?.source || '').toLowerCase() === 'manual';
  });
};

const countRulesBySource = (items = [], source) => {
  return (Array.isArray(items) ? items : []).filter((rule) => {
    return String(rule?.source || '').toLowerCase() === source;
  }).length;
};

const fetchStatus = async () => {
  const [strategyResponse, indexResponse] = await Promise.all([
    getFordDeliverStrategyStatus(),
    getFordDeliverVinIndexStatus(),
  ]);
  status.value = {
    ...status.value,
    ...unwrapData(strategyResponse),
    ...unwrapData(indexResponse),
  };
  if (!manualTotal.value) {
    manualTotal.value = countOf('V5075', 'manual') + countOf('V2', 'manual');
  }
  if (!uploadTotal.value) {
    uploadTotal.value = countOf('V5075', 'upload') + countOf('V2', 'upload');
  }
};

const fetchRules = async () => {
  const response = await getFordDeliverStrategyRules({
    strategyVersion: filters.strategyVersion || undefined,
    area: filters.area || undefined,
    page: rulePage.value,
    pageSize: rulePageSize.value,
  });
  const data = unwrapData(response);
  const rawRules = data.list || data.rules || [];
  const statusManualTotal = countOf('V5075', 'manual') + countOf('V2', 'manual');
  const statusUploadTotal = countOf('V5075', 'upload') + countOf('V2', 'upload');
  rules.value = normalizeManualRules(rawRules);
  manualTotal.value = data.manualTotal !== undefined
    ? Number(data.manualTotal || 0)
    : (statusManualTotal || countRulesBySource(rawRules, 'manual'));
  uploadTotal.value = data.uploadTotal !== undefined
    ? Number(data.uploadTotal || 0)
    : (statusUploadTotal || countRulesBySource(rawRules, 'upload'));
  rulePage.value = Number(data.page || rulePage.value);
  rulePageSize.value = Number(data.pageSize || rulePageSize.value);
};

const handleRuleFilterChange = async () => {
  rulePage.value = 1;
  await fetchRules();
};

const handleRulePageSizeChange = async () => {
  rulePage.value = 1;
  await fetchRules();
};

const fetchRedisStats = async () => {
  loadingRedisStats.value = true;
  try {
    const response = await getFordDeliverStrategyRedisStats();
    redisStats.value = unwrapData(response).stats || [];
  } catch (error) {
    ElMessage.error(error?.response?.data?.message || error.message || 'Redis 统计加载失败');
  } finally {
    loadingRedisStats.value = false;
  }
};

const fetchAll = async () => {
  loading.value = true;
  try {
    await Promise.all([fetchStatus(), fetchRules(), fetchRedisStats()]);
  } catch (error) {
    ElMessage.error(error?.response?.data?.message || error.message || '加载失败');
  } finally {
    loading.value = false;
  }
};

const readTextFile = (raw) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('文件读取失败'));
    reader.readAsText(raw);
  });
};

const chooseBatchFiles = () => {
  batchFilesInput.value?.click?.();
};

const rejectedBatchFileMessage = (item) => {
  if (item.reason === 'invalid_extension') {
    return `${item.name || '-'} 不是 txt 文件`;
  }
  if (item.reason === 'duplicate_area') {
    return `${item.name || '-'} 重复区域 ${item.area}`;
  }
  return `${item.name || '-'} 未识别 US / CHN / XX / OTHER`;
};

const readBatchFileSet = async (event) => {
  const input = event?.target;
  const { accepted, rejected } = validateDeliverBatchFiles(input?.files || []);

  if (rejected.length) {
    ElMessage.warning(rejected.map(rejectedBatchFileMessage).join('；'));
  }

  if (!accepted.length) {
    if (input) {
      input.value = '';
    }
    return;
  }

  try {
    const loadedAreas = [];
    for (const item of accepted) {
      batchAreas[item.area] = await readTextFile(item.file);
      loadedAreas.push(item.area);
    }
    ElMessage.success(`已载入 ${loadedAreas.join(' / ')} ${loadedAreas.length} 个文件`);
  } catch (error) {
    ElMessage.error(error?.message || '批量文件读取失败');
  } finally {
    if (input) {
      input.value = '';
    }
  }
};

const readAreaFile = (file, area) => {
  const raw = file?.raw;
  if (!raw)
    return;

  readTextFile(raw).then((text) => {
    batchAreas[area] = text;
    ElMessage.success(`${area} 文件已载入`);
  }).catch(() => {
    ElMessage.error(`${area} 文件读取失败`);
  });
};

const collectApiErrors = (error) => {
  const errors = error?.response?.data?.data?.errors || [];
  parseErrors.value = errors;
  return error?.response?.data?.message || error.message || '保存失败';
};

const queryRedisRowKeys = async (row) => {
  const rowKey = `${row.version}:${row.area}`;
  loadingRedisKeys.value = rowKey;
  try {
    const response = await getFordDeliverStrategyRedisKeys({
      version: row.version,
      area: row.area,
    });
    redisKeysResult.value = unwrapData(response);
  } catch (error) {
    ElMessage.error(error?.response?.data?.message || error.message || 'Redis Key 查询失败');
  } finally {
    loadingRedisKeys.value = '';
  }
};

const verifyRedis = async () => {
  verifyingRedis.value = true;
  try {
    const response = await verifyFordDeliverStrategyRedis({ issueLimit: 100 });
    redisVerifyResult.value = unwrapData(response);
    if (redisVerifyResult.value.consistent) {
      ElMessage.success('MySQL 启用规则与 Redis 当前值一致');
    } else {
      ElMessage.warning('MySQL 启用规则与 Redis 当前值存在差异');
    }
  } catch (error) {
    ElMessage.error(error?.response?.data?.message || error.message || 'Redis 校验失败');
  } finally {
    verifyingRedis.value = false;
  }
};

const submitBatch = async () => {
  if (batchRuleCount.value <= 0) {
    ElMessage.warning('请先上传或粘贴 V5075 规则');
    return;
  }

  savingBatch.value = true;
  parseErrors.value = [];
  try {
    const response = await uploadFordDeliverStrategyRules({
      areas: { ...batchAreas },
      remark: batchRemark.value,
    }, syncCopy.value);
    ElMessage.success(`已保存 ${unwrapData(response).inserted || 0} 条批量规则`);
    await Promise.all([fetchStatus(), fetchRules()]);
  } catch (error) {
    ElMessage.error(collectApiErrors(error));
  } finally {
    savingBatch.value = false;
  }
};

const clearBatch = () => {
  areaOptions.forEach((area) => {
    batchAreas[area.value] = '';
  });
  batchRemark.value = '';
};

const submitManual = async () => {
  if (manualRuleCount.value <= 0) {
    ElMessage.warning('请先填写手动规则');
    return;
  }

  savingManual.value = true;
  parseErrors.value = [];
  try {
    const response = await addFordDeliverStrategyManualRules({ ...manualForm }, syncCopy.value);
    ElMessage.success(`已保存 ${unwrapData(response).inserted || 0} 条手动规则`);
    manualForm.rulesText = '';
    rulePage.value = 1;
    await Promise.all([fetchStatus(), fetchRules()]);
  } catch (error) {
    ElMessage.error(collectApiErrors(error));
  } finally {
    savingManual.value = false;
  }
};

const clearManual = () => {
  manualForm.rulesText = '';
  manualForm.remark = '';
};

const deleteRule = async (row) => {
  try {
    await ElMessageBox.confirm(`确认删除 ${row.strategyVersion} ${row.area} ${row.pattern}？`, '删除规则', {
      confirmButtonText: '删除',
      cancelButtonText: '取消',
      type: 'warning',
    });
    await deleteFordDeliverStrategyRule(row.id, syncCopy.value);
    ElMessage.success('已删除');
    await Promise.all([fetchStatus(), fetchRules()]);
  } catch (error) {
    if (error === 'cancel' || error === 'close')
      return;
    ElMessage.error(error?.response?.data?.message || error.message || '删除失败');
  }
};

const handlePublish = async () => {
  publishing.value = true;
  try {
    const response = await publishFordDeliverStrategy(syncCopy.value);
    publishResult.value = unwrapData(response).publish || {};
    ElMessage.success('发布完成，VIN 索引已标记重建');
    await fetchAll();
  } catch (error) {
    ElMessage.error(error?.response?.data?.message || error.message || '发布失败');
  } finally {
    publishing.value = false;
  }
};

const rebuildVinDeliverIndex = async () => {
  if (rebuildingIndex.value) {
    return;
  }

  rebuildingIndex.value = true;
  indexRebuildProgress.value = {
    scanned: 0,
    matched: 0,
    skipped: 0,
  };

  let cursor = '';
  let reset = true;
  try {
    while (true) {
      const rebuildParams = {
        batch: 5000,
        reset: reset ? '1' : '0',
      };
      if (cursor) {
        rebuildParams.cursor = cursor;
      }

      const response = await rebuildFordDeliverVinIndex(rebuildParams);
      reset = false;
      const data = unwrapData(response);
      indexRebuildProgress.value = {
        scanned: Number(indexRebuildProgress.value.scanned || 0) + Number(data.scanned || 0),
        matched: Number(indexRebuildProgress.value.matched || 0) + Number(data.matched || 0),
        skipped: Number(indexRebuildProgress.value.skipped || 0) + Number(data.skipped || 0),
      };
      cursor = data.nextCursor || '';
      if (!data.hasMore || !cursor) {
        break;
      }
    }
    ElMessage.success('VIN Deliver 索引重建完成');
    await fetchStatus();
  } catch (error) {
    ElMessage.error(error?.response?.data?.message || error.message || 'VIN Deliver 索引重建失败');
  } finally {
    rebuildingIndex.value = false;
  }
};

const testVin = async () => {
  const vin = vinInput.value.trim().toUpperCase();
  if (vin.length !== 17) {
    ElMessage.warning('VIN 必须是 17 位');
    return;
  }

  testingVin.value = true;
  try {
    const response = await testFordDeliverStrategyVin({ vin });
    vinResult.value = unwrapData(response);
  } catch (error) {
    ElMessage.error(error?.response?.data?.message || error.message || 'VIN 测试失败');
  } finally {
    testingVin.value = false;
  }
};

onMounted(fetchAll);
</script>

<style scoped>
.deliver-strategy-panel {
  display: grid;
  gap: 14px;
  min-width: 0;
}

.panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  border: 1px solid #dfe8f3;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 10px 26px rgba(31, 56, 88, 0.06);
}

.panel-header h3,
.section-heading h4 {
  margin: 0;
  color: #132033;
  font-weight: 800;
  letter-spacing: 0;
}

.panel-header h3 {
  font-size: 20px;
}

.panel-header p,
.section-heading p {
  margin: 6px 0 0;
  color: #65758a;
  font-size: 13px;
  line-height: 1.45;
}

.header-actions,
.section-actions,
.vin-test-row,
.publish-result,
.vin-result,
.redis-keys-heading,
.redis-verify {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.status-strip {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(128px, 1fr));
  gap: 10px;
}

.status-item {
  min-height: 68px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid #dfe8f3;
  border-radius: 8px;
  background: #ffffff;
}

.status-item span {
  color: #65758a;
  font-size: 12px;
}

.status-item strong {
  color: #132033;
  font-size: 22px;
  line-height: 1;
}

.index-progress-line {
  margin-top: -4px;
  padding: 8px 12px;
  border: 1px solid #dfe8f3;
  border-radius: 8px;
  background: #f8fbfd;
  color: #486079;
  font-size: 13px;
  line-height: 1.45;
}

.work-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(320px, 0.65fr);
  gap: 14px;
  align-items: start;
}

.config-section {
  min-width: 0;
  padding: 16px;
  border: 1px solid #dfe8f3;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(31, 56, 88, 0.05);
}

.section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 14px;
}

.area-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.area-editor {
  min-width: 0;
}

.area-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.area-title strong {
  color: #25364b;
  font-size: 13px;
}

.remark-input,
.section-actions {
  margin-top: 12px;
}

.manual-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.full-control,
.list-filters :deep(.el-select) {
  width: 100%;
}

.list-filters {
  display: grid;
  grid-template-columns: repeat(2, 112px);
  gap: 8px;
}

.compact-actions {
  margin-top: 0;
}

.batch-upload-actions {
  justify-content: flex-end;
}

.hidden-file-input {
  display: none;
}

.redis-verify {
  min-height: 40px;
  margin-bottom: 12px;
  padding: 10px 12px;
  border: 1px solid #dfe8f3;
  border-radius: 8px;
  background: #f8fbfd;
  color: #334155;
  font-size: 13px;
}

.redis-stats-table {
  margin-bottom: 12px;
}

.redis-keys-heading {
  min-height: 40px;
  margin-bottom: 12px;
  padding: 10px 12px;
  border: 1px solid #dfe8f3;
  border-radius: 8px;
  background: #f8fbfd;
  color: #334155;
  font-size: 13px;
}

.redis-keys-table {
  margin-bottom: 12px;
}

.rule-table {
  width: 100%;
}

.rule-pagination {
  justify-content: flex-end;
  margin-top: 12px;
}

.mono {
  font-family: Consolas, Monaco, 'Courier New', monospace;
  letter-spacing: 0;
}

.vin-input {
  width: min(360px, 100%);
}

.vin-result,
.publish-result {
  min-height: 42px;
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid #dfe8f3;
  border-radius: 8px;
  background: #f8fbfd;
  color: #334155;
  font-size: 13px;
}

@media (max-width: 1180px) {
  .work-grid,
  .area-grid {
    grid-template-columns: 1fr;
  }

  .status-strip {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 720px) {
  .panel-header,
  .section-heading {
    flex-direction: column;
  }

  .status-strip,
  .manual-grid,
  .list-filters {
    grid-template-columns: 1fr;
  }

  .header-actions,
  .section-actions,
  .vin-test-row {
    align-items: stretch;
  }

  .header-actions > *,
  .section-actions > *,
  .vin-test-row > * {
    width: 100%;
  }
}
</style>
